import { useEffect, useState } from "react";
import CSDTable from "@cloudscape-design/components-themed/table";
import {
  Box,
  SpaceBetween,
  Header,
  Spinner,
  CollectionPreferencesProps,
} from "@cloudscape-design/components-themed";
import { getHeaderCounterText, getPagedItems } from "../../utils/common";
import { COMMON_LABELS } from "../default-constants";
import {
  LABELS,
  STATUS,
  CORPORATE_NUMBER,
  DUNS_NO,
  COMPANY_NAME,
  CONTRACT_SERVICE,
  MERCHANTS_TYPE,
  MERCHANTS_NAME,
  CONTRACT_TYPE,
  CONTRACT_ROUTE,
  QR_PRESENTATION_CATEGORY,
  IPID,
  SITE_ID,
  MERCHANT_MANAGEMENT_NUMBER,
  SHOP_ID,
  TERMINAL_IDENTIFICATION_NUMBER,
  // Orange項目追加
  //  決済グループID
  SETTLEMENT_GROUP_ID,
  //  Access Key
  ACCESS_KEY,
  //  Secret Key
  SECRET_KEY,
} from "./constants";
import { USER_TYPE, PERMISSIONS } from "../../configs/constants";
import type { RootState } from "../../app/store";
import {
  useGetMerchantListQuery,
  useLazyGetMerchantDownloadQuery,
  useLazyGetMerchantStoreDownloadQuery,
} from "../../features/api";
import Preference from "../UI/Preference";
import Pagination from "../UI/Pagination";
import { MerchantListSearchResponse } from "../../features/api-interfaces";
import { useAppDispatch, useAppSelector } from "../../app/hook";
import {
  columnDefinitionsBase,
  columnDefinitionsBaseNotProgress,
  columnDefinitionsBaseNotDocomo,
} from "./table-config";
import { Link } from "react-router-dom";
import { useCanIShowHook } from "../../app/useCanIShowHook";
import { ClickDetail } from "@cloudscape-design/components-themed/internal/events";
import { useHasAuthorityHook } from "../../app/useHasAuthorityHook";
import { IconButton } from "../UI/button/IconButton";
import { showModal } from "../UI/modal/modal-slice";
import {
  changePageSize,
  setColumnWidths,
  setPageIndex,
} from "../../features/slices/merchant-list";

interface ITable {
  isBackFromDetail: boolean;
  isSkipSearch: boolean;
}

const Table = (props: ITable) => {
  const { isBackFromDetail, isSkipSearch } = props;
  // ユーザ種別確認
  const hasAuthority = useHasAuthorityHook(PERMISSIONS.MASTER_EDIT);
  const isDocomoUser = useCanIShowHook(USER_TYPE.DOCOMO);
  const dispatch = useAppDispatch();

  // spinner・文字列表示有無
  const [
    isStoreDownloadSpinnerAndStrDisplay,
    setIsStoreDownloadSpinnerAndStrDisplay,
  ] = useState<boolean>(false);
  const [
    isMerchantDownloadSpinnerAndStrDisplay,
    setIsMerchantDownloadSpinnerAndStrDisplay,
  ] = useState<boolean>(false);

  // button表示有無
  const [isStoreDownloadButton, setIsStoreDownloadButton] =
    useState<boolean>(false);
  const [isMerchantDownloadButton, setIsMerchantDownloadButton] =
    useState<boolean>(false);

  const { searchCriteria, tableConfig } = useAppSelector(
    (state: RootState) => state.merchantList
  );
  const { pageSize, pageIndex, columnWidths } = tableConfig;

  //一覧データの取得
  const { data, isFetching } = useGetMerchantListQuery(searchCriteria, {
    skip: isDocomoUser && isSkipSearch,
  });

  /** テーブルカラム定義 */
  const columnItemBase = hasAuthority
    ? columnDefinitionsBase
    : isDocomoUser
    ? columnDefinitionsBaseNotProgress
    : columnDefinitionsBaseNotDocomo;

  const columnDefinitions = columnItemBase.map((column, index) => {
    let cell = (
      e: MerchantListSearchResponse
    ): JSX.Element | undefined | string => <></>;
    switch (column.id) {
      case STATUS:
        cell = (e: any) => e.status;
        break;
      case CORPORATE_NUMBER:
        cell = (e: any) => e.corporateNumber ?? "―";
        break;
      case DUNS_NO:
        cell = (e: any) => e.dunsNo;
        break;
      case COMPANY_NAME:
        cell = (e: any) => (
          <Link to={"/merchant/info-inquiry/" + e.companyCode}>
            {e.companyName}
          </Link>
        );
        break;
      case CONTRACT_SERVICE:
        cell = (e: any) => e.contractService;
        break;
      case MERCHANTS_TYPE:
        cell = (e: any) => e.merchantType;
        break;
      case MERCHANTS_NAME:
        cell = (e: any) =>
          (e.merchantName ?? "") + (e.merchantIdentifier ?? "");
        break;
      case CONTRACT_TYPE:
        cell = (e: any) => e.contractType;
        break;
      case CONTRACT_ROUTE:
        cell = (e: any) => e.contractRoute;
        break;
      case QR_PRESENTATION_CATEGORY:
        cell = (e: any) => e.qrPresentationCategory;
        break;
      case IPID:
        cell = (e: any) => e.ipid;
        break;
      case SITE_ID:
        cell = (e: any) => e.siteId;
        break;
      case MERCHANT_MANAGEMENT_NUMBER:
        cell = (e: any) => e.merchantManagementNumber;
        break;
      case SHOP_ID:
        cell = (e: any) => e.shopId;
        break;
      case TERMINAL_IDENTIFICATION_NUMBER:
        cell = (e: any) => e.terminalIdentificationNumber;
        break;
      // Orange項目追加
      //  決済グループID
      case SETTLEMENT_GROUP_ID:
        cell = (e: any) => e.settlementGroupId;
        break;
      //  Access Key
      case ACCESS_KEY:
        cell = (e: any) => e.accessKey;
        break;
      //  Secret Key
      case SECRET_KEY:
        cell = (e: any) => e.secretKey;
        break;
    }
    // カラム幅定義
    const width = isBackFromDetail ? columnWidths[index] : undefined;

    return { ...column, width, cell };
  });

  /**ページャ関連 */
  const options = columnDefinitions.map((column) => ({
    id: column.id,
    label: column.header,
  }));

  const [pagedItems, setPagedItems] = useState<Object[]>([]);
  const [allItems, setAllItems] = useState<Object[]>([]);

  useEffect(() => {
    if (data !== undefined) {
      setPagedItems(getPagedItems(data, pageIndex, pageSize));
      setAllItems(data);
    }
  }, [data, pageIndex, pageSize]);

  // ダウンロード関連変数
  const [lazyDownloadMerchantTrigger] = useLazyGetMerchantDownloadQuery();
  const [lazyDownloadMerchantStoreTrigger] =
    useLazyGetMerchantStoreDownloadQuery();

  const onClickMerchantDownloadButton = (e: CustomEvent<ClickDetail>) => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.DL_WAIT,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: () => {
          e.preventDefault();
          executeMerchantDownload();
        },
      })
    );
  };

  const executeMerchantDownload = async () => {
    // spinner・文字列活性
    setIsMerchantDownloadSpinnerAndStrDisplay(true);
    // button活性
    setIsMerchantDownloadButton(true);
    await lazyDownloadMerchantTrigger(searchCriteria)
      .unwrap()
      .then()
      .finally(() => {
        // spinner・文字列非活性
        setIsMerchantDownloadSpinnerAndStrDisplay(false);
        // button非活性
        setIsMerchantDownloadButton(false);
      });
  };

  const onClickStoreDownloadButton = (e: CustomEvent<ClickDetail>) => {
    dispatch(
      showModal({
        title: COMMON_LABELS.TITLES.DL,
        content: COMMON_LABELS.CONTENTS.DL_WAIT,
        positiveBtn: COMMON_LABELS.POSITIVE_BUTTON.OK,
        negativeBtn: COMMON_LABELS.NEGATIVE_BUTTON.CANCEL,
        positiveFn: () => {
          e.preventDefault();
          executeStoreDownload();
        },
      })
    );
  };

  const executeStoreDownload = async () => {
    // spinner・文字列活性
    setIsStoreDownloadSpinnerAndStrDisplay(true);
    // button活性
    setIsStoreDownloadButton(true);
    await lazyDownloadMerchantStoreTrigger(searchCriteria)
      .unwrap()
      .then()
      .finally(() => {
        // spinner・文字列非活性
        setIsStoreDownloadSpinnerAndStrDisplay(false);
        // button非活性
        setIsStoreDownloadButton(false);
      });
  };

  /**
   * テーブルのカラム幅を変更したときの処理.
   * @param {number[]} widths カラム幅リスト
   */
  const onColumnWidthsChange = (widths: number[]) =>
    dispatch(setColumnWidths(widths));

  /**
   * ページ番号を変更したときの処理.
   * @param {number} currentPageIndex 現在のページ番号
   */
  const setCurrentPageIndex = (currentPageIndex: number) =>
    dispatch(setPageIndex(currentPageIndex));

  /**
   * ページサイズを変更したときの処理.
   * @param {CollectionPreferencesProps.Preferences} detail
   */
  const handleOnConfirm = (detail: CollectionPreferencesProps.Preferences) =>
    dispatch(changePageSize(detail.pageSize!));

  return (
    <div>
      <CSDTable
        columnDefinitions={columnDefinitions}
        loading={isFetching}
        loadingText={COMMON_LABELS.CSD.LOADING_TEXT}
        items={pagedItems}
        onColumnWidthsChange={({ detail: { widths } }) =>
          onColumnWidthsChange(widths as number[])
        }
        resizableColumns
        header={
          <Header
            actions={
              <SpaceBetween size={"xxl"} direction={"horizontal"}>
                <SpaceBetween direction="horizontal" size="xs">
                  {isMerchantDownloadSpinnerAndStrDisplay ? (
                    <Spinner size="big" />
                  ) : (
                    <></>
                  )}
                  {isMerchantDownloadSpinnerAndStrDisplay ? (
                    <>{COMMON_LABELS.CONTENTS.DL_RUN}</>
                  ) : (
                    <></>
                  )}
                  <IconButton
                    iconName={"download"}
                    label={LABELS.MERCHANTS_LIST + COMMON_LABELS.BTNS.DL}
                    disabled={isMerchantDownloadButton}
                    onClick={onClickMerchantDownloadButton}
                  />
                </SpaceBetween>
                <SpaceBetween direction="horizontal" size="xs">
                  {isStoreDownloadSpinnerAndStrDisplay ? (
                    <Spinner size="big" />
                  ) : (
                    <></>
                  )}
                  {isStoreDownloadSpinnerAndStrDisplay ? (
                    <>{COMMON_LABELS.CONTENTS.DL_RUN}</>
                  ) : (
                    <></>
                  )}
                  <IconButton
                    iconName={"download"}
                    label={LABELS.STORE_LIST + COMMON_LABELS.BTNS.DL}
                    disabled={isStoreDownloadButton}
                    onClick={onClickStoreDownloadButton}
                  />
                </SpaceBetween>
              </SpaceBetween>
            }
          >
            {LABELS.MERCHANTS_LIST}
            {getHeaderCounterText(allItems.length, pagedItems.length)}
          </Header>
        }
        pagination={
          <Pagination
            currentPageIndex={pageIndex}
            filteredItems={allItems}
            setCurrentPageIndex={setCurrentPageIndex}
            pageSize={pageSize}
            setPagedItems={setPagedItems}
          />
        }
        preferences={
          <Preference
            preferences={{ pageSize, visibleContent: [] }}
            options={options}
            handleOnConfirm={handleOnConfirm}
          />
        }
        empty={
          <Box textAlign="center" color="inherit">
            {COMMON_LABELS.CSD.NO_DATA}
          </Box>
        }
      />
    </div>
  );
};

export default Table;
